import {
  FC,
  forwardRef,
  useRef,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  SyntheticEvent,
  type CSSProperties,
  type ReactNode,
  type Ref,
} from "react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { MultiSelectComponentProps } from "../../interface";

type Option = Record<string, unknown>;

/**
 * Syncfusion-compat payload for `select` / `removed`. `itemData` is the raw datasource entry (an
 * option object, or the raw string when the datasource is a string array).
 *
 * TRIED and REVERTED (measured: +5 errors): method shorthand on `select`/`removed` plus an
 * all-optional `{ itemData?: unknown }`. Bivariance did fix the annotated `select`/`removed`
 * handlers, but this payload is also READ by consumers, and that is the direction the asymmetry
 * rule cannot help:
 *   1. `valueTemplate` is declared `(item: SelectEventArgs["itemData"]) => ReactNode` -- a property
 *      arrow, so contravariant -- and ReviewFlowReviewLevelProperties (l.1772, l.1878) annotates it
 *      `(args: ReviewFlowStatus)`. `unknown` is not assignable to `ReviewFlowStatus`.
 *   2. RESOLVED: FilesCustomFields's inline `select`/`removed` handlers now assert the payload
 *      themselves, so they no longer depend on `itemData` being `any`.
 * Only blocker 1 remains; closing it needs `valueTemplate`'s consumer changed.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- read-direction payload: `valueTemplate` is a property arrow annotated `(args: ReviewFlowStatus)` at ReviewFlowReviewLevelProperties; measured, see comment above
type SelectEventArgs = { itemData: any; name: string; cancel: boolean };

/** Props consumers pass that `MultiSelectComponentProps` (Syncfusion era) does not declare. */
interface MultiSelectExtraProps {
  value?: unknown;
  select?: (args: SelectEventArgs) => void;
  removed?: (args: SelectEventArgs) => void;
  /** Receives the same untyped datasource entry as `select`; see `SelectEventArgs`. */
  valueTemplate?: (item: SelectEventArgs["itemData"]) => ReactNode;
  tagging?: (args: { setClass: (cssClass: string) => void }) => void;
  created?: () => void;
  width?: string | number;
  className?: string;
  disabled?: boolean;
  allowCustomValue?: boolean;
  showClearButton?: boolean;
  /** Syncfusion-only; accepted, forwarded through `...rest` and ignored by Autocomplete. */
  changeOnBlur?: boolean;
}

const isStringArray = (arr: unknown[]): arr is string[] => arr.length === 0 || typeof arr[0] === "string";

/** A datasource / value entry that is not a primitive is already an option object. Written as a
 *  predicate so the option shape is proved rather than asserted onto an untyped entry. */
const isOptionObject = (entry: unknown): entry is Option => typeof entry === "object" && entry !== null;

const MultiSelectComponent: FC<MultiSelectComponentProps & MultiSelectExtraProps> = forwardRef(
  (props: MultiSelectComponentProps & MultiSelectExtraProps, ref: Ref<unknown>) => {
    const {
      dataSource = [],
      fields = { text: "text", value: "value" },
      value: valueProp,
      placeholder,
      enabled = true,
      change,
      onChange,
      select,
      removed,
      width,
      widthInPercentage,
      allowCustomValue,
      mode: _mode,
      created,
      valueTemplate,
      tagging: _tagging,
      showClearButton,
      disableChipBackground,
      ...rest
    } = props;

    const autocompleteRef = useRef<HTMLDivElement | null>(null);

    const rawOptions: unknown[] = Array.isArray(dataSource) ? dataSource : [];
    const stringMode = isStringArray(rawOptions);
    const textField = fields.text || "text";
    const valueField = fields.value || "value";

    // Normalize string arrays into objects so the rest of the logic is uniform
    const normalizeOptions = useCallback(
      (raw: unknown[]): Option[] =>
        isStringArray(raw)
          ? raw.map((s) => ({ [valueField]: s, [textField]: s }))
          : // A datasource whose first entry is not a string may still hold loose primitives further
            // in, so each entry is checked and primitives are wrapped the same way as string mode.
            raw.map((entry) => (isOptionObject(entry) ? entry : { [valueField]: entry, [textField]: entry })),
      [valueField, textField],
    );

    const options: Option[] = normalizeOptions(rawOptions);

    // Resolve a value prop (array of primitives or objects) into Option[]
    const resolveFromProp = useCallback(
      (val: unknown): Option[] => {
        if (!val) return [];
        // `Array.isArray` on an `unknown` narrows to `any[]`; annotate so the entries stay `unknown`.
        const arr: unknown[] = Array.isArray(val) ? val : [val];
        return arr.map((v) => {
          if (isOptionObject(v)) return v;
          return options.find((o) => o[valueField] === v) ?? { [valueField]: v, [textField]: v };
        });
      },
      [options, valueField, textField],
    );

    // Internal state — source of truth for the selected chips
    const [selected, setSelected] = useState<Option[]>(() => resolveFromProp(valueProp));

    // Sync internal state when the external value prop changes
    const prevValuePropRef = useRef(valueProp);
    useEffect(() => {
      if (prevValuePropRef.current !== valueProp) {
        prevValuePropRef.current = valueProp;
        setSelected(resolveFromProp(valueProp));
      }
    }, [valueProp, resolveFromProp]);

    // Expose imperative handle so consumers can do ref.current.value = [...] (Syncfusion compat)
    useImperativeHandle(ref, () => {
      // The imperative handle is the Autocomplete root node (or a bare object before mount) with a
      // Syncfusion-style `value` accessor bolted on, hence the object-typed union.
      const handle: HTMLDivElement | object = autocompleteRef.current ?? {};
      Object.defineProperty(handle, "value", {
        get: () => selected.map((o) => o[valueField]),
        set: (newVal: unknown) => {
          setSelected(resolveFromProp(newVal));
        },
        configurable: true,
      });
      return handle;
    }, [selected, valueField, resolveFromProp]);

    // Fire `created` callback on mount (Syncfusion compat)
    const createdRef = useRef(created);
    createdRef.current = created;
    useEffect(() => {
      if (createdRef.current) createdRef.current();
    }, []);

    const getRawValue = (o: Option) => o[valueField];

    const handleChange = (_: SyntheticEvent, newValue: (Option | string)[]) => {
      // freeSolo can return raw strings for newly typed values — normalize them
      const normalized: Option[] = newValue.map((v) => {
        if (typeof v === "string") return { [valueField]: v, [textField]: v } as Option;
        return v as Option;
      });

      const prev = selected;
      const values = normalized.map((o) => getRawValue(o));

      // Detect added item
      if (normalized.length > prev.length) {
        const added = normalized.at(-1);
        const callbackItem = stringMode ? getRawValue(added!) : added;
        if (select) {
          const args: SelectEventArgs = { itemData: callbackItem, name: "select", cancel: false };
          select(args);
          if (args.cancel) return;
        }
      }

      // Detect removed item
      if (normalized.length < prev.length) {
        const removedItem = prev.find((p) => !normalized.some((n) => n[valueField] === p[valueField]));
        if (removedItem && removed) {
          const callbackItem = stringMode ? getRawValue(removedItem) : removedItem;
          const args: SelectEventArgs = { itemData: callbackItem, name: "removed", cancel: false };
          removed(args);
          if (args.cancel) return;
        }
      }

      // Update internal state
      setSelected(normalized);

      if (change) change({ value: values, itemData: stringMode ? values : normalized });
      if (onChange) onChange({ value: values, itemData: stringMode ? values : normalized });
    };

    const containerStyle: CSSProperties = {
      width: width || widthInPercentage || "100%",
    };

    const filter = createFilterOptions<Option>();

    return (
      <div style={containerStyle}>
        <Autocomplete
          ref={autocompleteRef}
          multiple
          freeSolo={!!allowCustomValue}
          options={options}
          value={selected}
          disabled={!enabled}
          disableClearable={showClearButton === false}
          getOptionLabel={(option: Option | string) => {
            if (typeof option === "string") return option;
            return String(option[textField] ?? "");
          }}
          isOptionEqualToValue={(option: Option, val: Option | string) =>
            // MUI v9 widened this parameter to include the free-solo string. `value` here is always
            // the normalized `Option[]`, so that branch is unreachable; it compares against the raw
            // value rather than silently returning false.
            option[valueField] === (typeof val === "string" ? val : val[valueField])
          }
          filterOptions={
            allowCustomValue
              ? (opts, params) => {
                  const filtered = filter(opts, params);
                  const { inputValue } = params;
                  if (inputValue !== "" && !opts.some((o) => o[valueField] === inputValue)) {
                    filtered.push({ [valueField]: inputValue, [textField]: inputValue } as Option);
                  }
                  return filtered;
                }
              : undefined
          }
          onChange={handleChange}
          // MUI v9 renamed Autocomplete's `renderTags`/`getTagProps` to `renderValue`/`getItemProps`.
          renderValue={(tagValue, getItemProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getItemProps({ index });

              // Support custom valueTemplate (Syncfusion compat)
              if (valueTemplate) {
                return (
                  <span key={key} {...tagProps}>
                    {valueTemplate(typeof option === "string" ? { [valueField]: option, [textField]: option } : option)}
                  </span>
                );
              }

              const label = typeof option === "string" ? option : String(option[textField] ?? "");
              return (
                <Chip
                  key={key}
                  label={label}
                  {...tagProps}
                  size="small"
                  sx={disableChipBackground ? { backgroundColor: "transparent", border: "none" } : undefined}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextFieldComponent
              {...params}
              placeholder={selected.length === 0 ? (placeholder as string | undefined) : undefined}
            />
          )}
          {...rest}
        />
      </div>
    );
  },
);

MultiSelectComponent.displayName = "MultiSelectComponent";
export default MultiSelectComponent;
